import { messagingApi } from '@/api/MessagingApi'
import type { ChatListMessagesResponse, ChatMessage } from '@/api/types/MessagingApi'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { CHAT_MESSAGES_QUERY_KEY } from './useChatMessages'

/**
 * Tells the server which of Wally's replies the patient has now seen.
 *
 * Load-bearing, not cosmetic: an unread reply left unconsumed is emailed to the patient as a
 * digest. Each id is sent once per mount, however often the list is polled.
 */
const useMarkChatMessagesConsumed = (messages: ChatMessage[], enabled: boolean) => {
	const queryClient = useQueryClient()
	const sentIds = useRef(new Set<number>())

	const { mutate } = useMutation({
		mutationFn: (messageIds: number[]) =>
			messagingApi.markChatMessagesConsumed(messageIds),
		// Seen is seen: the cache says so at once, and the badge goes with it.
		onMutate: (messageIds) => {
			const now = new Date().toISOString()
			queryClient.setQueryData<ChatListMessagesResponse>(
				CHAT_MESSAGES_QUERY_KEY,
				(old) =>
					old
						? {
								...old,
								messages: old.messages.map((m) =>
									messageIds.includes(m.id) && m.consumedAt === null
										? { ...m, consumedAt: now }
										: m
								),
						  }
						: old
			)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CHAT_MESSAGES_QUERY_KEY })
		},
	})

	useEffect(() => {
		if (!enabled) {
			return
		}
		const unread = messages
			.filter(
				(message) =>
					message.direction === 'OUTGOING' &&
					message.consumedAt === null &&
					!sentIds.current.has(message.id)
			)
			.map((message) => message.id)
		if (unread.length === 0) {
			return
		}
		for (const id of unread) {
			sentIds.current.add(id)
		}
		mutate(unread)
	}, [messages, enabled, mutate])
}

export { useMarkChatMessagesConsumed }
